/**
* Header — Mobile UX layer roles:
*
* TOP (Header) Logo · Search · Notifications · Hamburger
* BOTTOM (BottomNav) Home · Search · Bookings · Profile ← PRIMARY NAV
* FLOAT (HelpWidget) Support/help only
*
* Mobile hamburger MUST NOT duplicate BottomNav destinations.
* Hamburger contains: secondary discovery (Become a Helper, How It Works,
* Safety, FAQ) + preferences (Language) + account utilities + Sign Out.
*
* First-time user journeys (mobile UX checklist):
* 1. DISCOVER → Homepage hero → service categories → value prop clear
* 2. SEARCH → Tap Search (header or BottomNav) → enter query → results
* 3. VIEW → Tap helper card → full profile (reviews/services/rates)
* 4. BOOK → Tap "Book Now" on profile → wizard → confirmation screen
* 5. PAY → Receive payment request → mobile money → receipt
*/

import { Button } from "@/components/ui/button";
import { KanfyLogo } from "@/components/KanfyLogo";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { openHelp } from "@/lib/supportChat";
import { Menu, Search, User, LogOut, ChevronDown, Globe, Settings, LayoutDashboard, UserCircle, MessageSquare, Calendar, Shield, Bell, HelpCircle, Sparkles, BookOpen, Briefcase, BookMarked, ClipboardList, Sun, Moon, Check } from "lucide-react";
import { Sparkle } from "@/components/ui/Sparkle";
import { useTheme } from "@/contexts/ThemeContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { onAvatarUpdated } from "@/lib/profileAvatarEvents";
import { SearchDialog } from "./SearchDialog";
import { AuthModal } from "./auth/AuthModal";
import { NotificationDropdown } from "./NotificationDropdown";
import { useAuth } from "@/hooks/useAuth";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useNavigate, useLocation } from 'react-router';
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/useLanguage";
import { useTranslation } from "@/hooks/useTranslation";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { supabase } from "@/integrations/supabase/client";
import { DEMO_MODE } from "@/lib/demoMode";
import { useState, useEffect, useCallback } from "react";
import { useModeSwitch } from "@/components/animations/ModeSwitchProvider";
import { logger } from "@/lib/logger";
import { isNative } from "@/lib/env";
import { MobileTopBar } from "@/mobile/MobileTopBar";
import { OrbitSparkle } from '@/components/ui/Sparkle';

/**
 * The native shell replaces the website header with a slim top bar
 * (src/mobile/MobileTopBar.tsx). `isNative` is a build-time constant, so the
 * web bundle keeps exactly the component below and nothing else.
 */
export function Header() {
  return isNative ? <MobileTopBar /> : <WebHeader />;
}

function WebHeader() {
const { user, signOut } = useAuth();
const { isAdmin, hasRole, isSuperAdmin } = useAdminAuth();
const navigate = useNavigate();
const location = useLocation();
const { toast } = useToast();
const { language, setLanguage, getLanguageLabel } = useLanguage();
const { t } = useTranslation();
// Render only ONE NotificationDropdown at a time to prevent the dual
// Supabase auth-token lock contention that causes "Lock was stolen" errors.
const isDesktop = useMediaQuery("(min-width: 640px)");
const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
// This header's phone menu carries "Help & support", so while it is on the
// page the floating Help button can stand down on a phone (index.css,
// .help-fab). Pages without this header keep the floating button (Codex,
// PR #1274: /auth, /reset-password, /platform-rules).
useEffect(() => {
document.documentElement.dataset.webHeader = 'true';
return () => {
delete document.documentElement.dataset.webHeader;
};
}, []);
const [profileMode, setProfileMode] = useState<'helper' | 'client' | null>(null);
// The Client<->Helper transition is owned by the app-level ModeSwitchProvider so
// it survives the navigation; Header just triggers it.
const { playModeSwitch, isSwitching } = useModeSwitch();
// Bug 7: authoritative first name from profiles table (overrides auth metadata
const { resolvedTheme, setTheme } = useTheme();
// which can get out of sync, e.g. old test accounts with "Test" as first_name)
const [profileFirstName, setProfileFirstName] = useState<string | null>(null);
const [profileAvatarUrl, setProfileAvatarUrl] = useState<string | null>(null);
const [isProVerified, setIsProVerified] = useState(false);
const [helperProfileStatus, setHelperProfileStatus] = useState<string | null>(null);

// Derive mode from route — no network call needed
useEffect(() => {
if (!user) {
setProfileMode(null);
return;
}
const isOnHelperRoute =
location.pathname.includes('/helper-dashboard') || location.pathname.includes('/dashboard/helper') ||
location.pathname.includes('/helper-account') ||
location.pathname.includes('/helper-profile') ||
location.pathname.includes('/onboarding');
setProfileMode(isOnHelperRoute ? 'helper' : 'client');
}, [user, location.pathname]);

// Bug 7: fetch authoritative first_name from profiles table once on sign-in.
// Auth user_metadata.first_name can be stale (e.g. "Test" for old accounts).
useEffect(() => {
if (!user) { setProfileFirstName(null); setProfileAvatarUrl(null); setHelperProfileStatus(null); return; }
supabase
.from('profiles')
.select('first_name, avatar_url, profile_status')
.eq('id', user.id)
.maybeSingle()
.then(({ data }) => {
if (data?.first_name) setProfileFirstName(data.first_name);
if (data?.avatar_url) setProfileAvatarUrl(data.avatar_url);
if ((data as any)?.pro_verified) setIsProVerified(true);
setHelperProfileStatus(data?.profile_status ?? null);
});
}, [user?.id]);

// A photo uploaded in this session replaces the one read at sign-in.
useEffect(() => {
if (!user?.id) return;
return onAvatarUpdated(user.id, setProfileAvatarUrl);
}, [user?.id]);

const handleModeSwitch = useCallback(async () => {
if (isSwitching || !user) return;

const target = profileMode === 'client' ? 'helper' : 'client';

try {
await playModeSwitch({
target,
// Runs under the intro animation; returns where to land.
resolveDestination: async () => {
if (DEMO_MODE) {
return profileMode === 'client' ? '/helper-dashboard' : '/';
}
if (profileMode !== 'client') {
return '/client-dashboard';
}
const { data, error } = await supabase
.from('profiles')
.select('is_helper, profile_status, profile_completed')
.eq('id', user.id)
.maybeSingle();
if (error) throw error;
if (!data?.is_helper) return '/helper-profile';
if (data.profile_status === 'pending') {
toast({ title: 'Profile Under Review', description: 'Your helper profile is being reviewed.' });
return '/helper-profile';
}
if (data.is_helper && data.profile_completed && data.profile_status === 'approved') {
return '/helper-dashboard';
}
return '/helper-profile';
},
});
} catch {
toast({ title: 'Error', description: 'Could not switch mode. Please try again.', variant: 'destructive' });
}
}, [isSwitching, user, profileMode, playModeSwitch, toast]);

const handleOpenAuthModal = () => {
setIsAuthModalOpen(true);
};

const handleSignOut = async () => {
try {
const { error } = await signOut();
if (error) {
logger.error('Sign out failed', error);
toast({ title: "Sign out failed", description: error.message, variant: "destructive" });
}
} catch (err) {
logger.error('Unexpected error during sign out', err);
toast({ title: "Sign out failed", description: "An unexpected error occurred", variant: "destructive" });
}
};

return (
<header
className="sticky z-50 w-full border-b bg-background/75 backdrop-blur-xl border-border/40 shadow-sm"
style={{ top: 'var(--staging-banner-height, 0px)' }}
role="banner"
>
<div className="container mx-auto px-3 sm:px-4 lg:px-8">
<div className="flex h-[64px] sm:h-[72px] items-center justify-between gap-2">

{/* ── Logo ─────────────────────────────────────────────── */}
{/* shrink-0 on the click target: with min-w-0, the flex fight against the
    (also shrink-0) mode-toggle pill collapsed this div to 0px and the
    wordmark painted UNDER the pill. The logo must never shrink — instead
    the wordmark is dropped below lg (mark-only) so tighter headers fit. */}
<div className="flex items-center gap-2 flex-1 md:flex-initial min-w-0">
<div
className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity shrink-0"
onClick={() => navigate('/')}
role="link"
tabIndex={0}
onKeyDown={(e) => {
if (e.key === 'Enter' || e.key === ' ') {
e.preventDefault();
navigate('/');
}
}}
aria-label="Go to Kanfy homepage"
>
{/* The nav is cream on light and near-black teal on dark, so the mark
    follows it: full colour on cream, reversed on ink. The knockout is
    the nav colour so the door reads as a cutout either way. */}
<span className="hidden lg:inline-flex"><KanfyLogo variant="auto" size="md" /></span>
<span className="lg:hidden inline-flex"><KanfyLogo variant="auto" size="md" showWordmark={false} /></span>
</div>

{/* Mode Toggle — desktop + sm only */}
{user && profileMode && (
<Button
variant="outline"
size="sm"
onClick={handleModeSwitch}
disabled={isSwitching}
className="ml-2 hidden xl:inline-flex shrink-0 overflow-hidden text-xs px-3 h-8 rounded-full border-primary/30 text-primary bg-primary/5 hover:bg-primary/15"
>
{/* Header-row budget: the Kanfy lockup is ~68px wider than the old badge,
    so at lg (row 960px: lockup 179 + nav 335 + controls 489) this pill
    overflowed and painted over the nav. Below xl the mode switch lives in
    the account dropdown (which always has it); the pill only renders at xl+
    where the container widens enough to fit it. */}
<span>{profileMode === 'helper' ? '⇄ Switch to Client' : '⇄ Switch to Helper'}</span>
</Button>
)}
</div>

{/* ── Desktop Nav ───────────────────────────────────────── */}
<nav className="hidden lg:flex items-center gap-6" aria-label="Main navigation">
<DropdownMenu>
<DropdownMenuTrigger asChild>
<Button variant="ghost" aria-haspopup="menu" aria-label="For Customers menu" className="rounded-full hover:bg-primary/10 font-medium">
For Customers <ChevronDown className="h-4 w-4 ml-1" aria-hidden="true" />
</Button>
</DropdownMenuTrigger>
<DropdownMenuContent className="bg-background border border-border shadow-lg z-50 w-56">
<DropdownMenuItem onClick={() => navigate('/booking-flow')} className="hover:bg-accent cursor-pointer font-medium">
Browse All Helpers
</DropdownMenuItem>
<DropdownMenuItem onClick={() => navigate('/how-it-works')} className="hover:bg-accent cursor-pointer">
How It Works
</DropdownMenuItem>
<DropdownMenuItem onClick={() => navigate('/safety')} className="hover:bg-accent cursor-pointer">
Safety &amp; Trust
</DropdownMenuItem>
<DropdownMenuItem onClick={() => navigate('/faq')} className="hover:bg-accent cursor-pointer">
FAQs
</DropdownMenuItem>
<DropdownMenuSeparator />
<DropdownMenuLabel className="text-xs text-muted-foreground">Browse by Category</DropdownMenuLabel>
<DropdownMenuItem onClick={() => navigate('/childcare')} className="hover:bg-accent cursor-pointer">
{t('childcareServices')}
</DropdownMenuItem>
<DropdownMenuItem onClick={() => navigate('/elderly-care')} className="hover:bg-accent cursor-pointer">
{t('elderlyCare')}
</DropdownMenuItem>
<DropdownMenuItem onClick={() => navigate('/cleaning')} className="hover:bg-accent cursor-pointer">
{t('cleaningServices')}
</DropdownMenuItem>
<DropdownMenuItem onClick={() => navigate('/tutoring')} className="hover:bg-accent cursor-pointer">
{t('tutoringServices')}
</DropdownMenuItem>
<DropdownMenuItem onClick={() => navigate('/pet-care')} className="hover:bg-accent cursor-pointer">
{t('petCareServices')}
</DropdownMenuItem>
<DropdownMenuItem onClick={() => navigate('/security')} className="hover:bg-accent cursor-pointer">
Security Services
</DropdownMenuItem>
<DropdownMenuItem onClick={() => navigate('/booking-flow')} className="hover:bg-accent cursor-pointer border-t font-medium">
{t('browseAllServices')}
</DropdownMenuItem>
</DropdownMenuContent>
</DropdownMenu>

<DropdownMenu>
<DropdownMenuTrigger asChild>
<Button variant="ghost" aria-haspopup="menu" aria-label="For Helpers menu" className="rounded-full hover:bg-primary/10 font-medium">
For Helpers <ChevronDown className="h-4 w-4 ml-1" aria-hidden="true" />
</Button>
</DropdownMenuTrigger>
<DropdownMenuContent className="bg-background border border-border shadow-lg z-50 w-56">
{/* Staff accounts can't become helpers (enforce_no_helper_on_staff DB
    trigger blocks it outright) — don't nudge them toward a CTA that only
    ends in a rejected submission. */}
{!isAdmin && (
<DropdownMenuItem onClick={() => navigate('/become-helper')} className="hover:bg-accent cursor-pointer font-medium">
Become a Helper
</DropdownMenuItem>
)}
<DropdownMenuItem onClick={() => navigate('/helper-resources')} className="hover:bg-accent cursor-pointer">
Helper Resources
</DropdownMenuItem>
<DropdownMenuItem onClick={() => navigate('/how-it-works')} className="hover:bg-accent cursor-pointer">
How It Works
</DropdownMenuItem>
{/* "Find Jobs" (/jobs board) removed: the job board was deleted and its
    old URLs redirect home (see App.tsx). */}
</DropdownMenuContent>
</DropdownMenu>
</nav>

{/* ── Right side controls ───────────────────────────────── */}
<div className="flex items-center gap-1 sm:gap-2">

{/* Theme Toggle — desktop */}
<button
onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
className="hidden md:flex h-9 w-9 items-center justify-center rounded-full text-foreground/70 hover:text-foreground hover:bg-muted transition-colors"
>
{resolvedTheme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
</button>

{/* Search icon */}
{profileMode !== 'helper' && (
<SearchDialog>
<Button variant="ghost" size="icon" className="h-11 w-11 text-foreground/70 hover:text-foreground hover:bg-muted" aria-label="Search for services">
<Search className="h-5 w-5" />
</Button>
</SearchDialog>
)}

{/* Language — desktop only */}
<DropdownMenu>
<DropdownMenuTrigger asChild>
<Button variant="ghost" size="sm" className="hidden md:flex text-white/80 hover:text-white hover:bg-white/10" aria-label={`Change language. Current: ${getLanguageLabel(language)}`}>
<Globe className="h-4 w-4 mr-1" aria-hidden="true" />
{language.toUpperCase()}
</Button>
</DropdownMenuTrigger>
<DropdownMenuContent className="bg-background border border-border shadow-lg z-50">
<DropdownMenuItem className="hover:bg-accent hover:text-accent-foreground cursor-pointer" onClick={() => setLanguage('en-UG')}>🇺🇬 English (UG)</DropdownMenuItem>
<DropdownMenuItem className="hover:bg-accent hover:text-accent-foreground cursor-pointer" onClick={() => setLanguage('en-US')}>🇺🇸 English (US)</DropdownMenuItem>
<DropdownMenuItem className="hover:bg-accent hover:text-accent-foreground cursor-pointer" onClick={() => setLanguage('fr')}>🇫🇷 French</DropdownMenuItem>
<DropdownMenuItem className="hover:bg-accent hover:text-accent-foreground cursor-pointer" onClick={() => setLanguage('sw')}>🇰🇪 Swahili</DropdownMenuItem>
<DropdownMenuItem className="hover:bg-accent hover:text-accent-foreground cursor-pointer" onClick={() => setLanguage('ar')}>🇸🇦 Arabic</DropdownMenuItem>
</DropdownMenuContent>
</DropdownMenu>

{/* Admin dropdown — desktop only */}
{isAdmin && (
<DropdownMenu>
<DropdownMenuTrigger asChild>
<Button variant="ghost" size="sm" className="hidden lg:flex rounded-full">
<Shield className="h-4 w-4 mr-1" />
Admin
</Button>
</DropdownMenuTrigger>
<DropdownMenuContent align="end" className="w-56 bg-background border border-border shadow-lg z-50">
<DropdownMenuLabel className="flex items-center gap-2">
<Shield className="h-4 w-4" />
Admin Portal
</DropdownMenuLabel>
<DropdownMenuSeparator />
<DropdownMenuItem onClick={() => navigate('/admin')} className="hover:bg-accent hover:text-accent-foreground cursor-pointer">
<LayoutDashboard className="h-4 w-4 mr-2" />
Dashboard
</DropdownMenuItem>
<DropdownMenuSeparator />
<DropdownMenuLabel className="text-xs text-muted-foreground">Management</DropdownMenuLabel>
<DropdownMenuItem onClick={() => navigate('/admin/helpers')} className="hover:bg-accent hover:text-accent-foreground cursor-pointer">
<UserCircle className="h-4 w-4 mr-2" />
Helper Management
</DropdownMenuItem>
<DropdownMenuItem onClick={() => navigate('/admin/customers')} className="hover:bg-accent hover:text-accent-foreground cursor-pointer">
<User className="h-4 w-4 mr-2" />
Customers
</DropdownMenuItem>
<DropdownMenuItem onClick={() => navigate('/admin/tasks')} className="hover:bg-accent hover:text-accent-foreground cursor-pointer">
<Calendar className="h-4 w-4 mr-2" />
Tasks &amp; Bookings
</DropdownMenuItem>
{(isSuperAdmin() || hasRole('finance_manager')) && (
<DropdownMenuItem onClick={() => navigate('/admin/financial')} className="hover:bg-accent hover:text-accent-foreground cursor-pointer">
<Settings className="h-4 w-4 mr-2" />
Financial
</DropdownMenuItem>
)}
{(isSuperAdmin() || hasRole('trust_safety_manager') || hasRole('operations_manager')) && (
<DropdownMenuItem onClick={() => navigate('/admin/trust-safety')} className="hover:bg-accent hover:text-accent-foreground cursor-pointer">
<Shield className="h-4 w-4 mr-2" />
Trust &amp; Safety
</DropdownMenuItem>
)}
<DropdownMenuSeparator />
<DropdownMenuLabel className="text-xs text-muted-foreground">Tools</DropdownMenuLabel>
<DropdownMenuItem onClick={() => navigate('/admin/support')} className="hover:bg-accent hover:text-accent-foreground cursor-pointer">
<MessageSquare className="h-4 w-4 mr-2" />
Support
</DropdownMenuItem>
<DropdownMenuItem onClick={() => navigate('/admin/analytics')} className="hover:bg-accent hover:text-accent-foreground cursor-pointer">
<LayoutDashboard className="h-4 w-4 mr-2" />
Analytics
</DropdownMenuItem>
{isSuperAdmin() && (
<>
<DropdownMenuSeparator />
<DropdownMenuItem onClick={() => navigate('/admin/settings')} className="hover:bg-accent hover:text-accent-foreground cursor-pointer">
<Settings className="h-4 w-4 mr-2" />
Settings
</DropdownMenuItem>
<DropdownMenuItem onClick={() => navigate('/admin-utils')} className="hover:bg-accent hover:text-accent-foreground cursor-pointer">
<Settings className="h-4 w-4 mr-2" />
Admin Utilities
</DropdownMenuItem>
<DropdownMenuItem onClick={() => navigate('/admin/logs')} className="hover:bg-accent hover:text-accent-foreground cursor-pointer">
<MessageSquare className="h-4 w-4 mr-2" />
System Logs
</DropdownMenuItem>
</>
)}
<DropdownMenuSeparator />
<DropdownMenuItem onClick={handleSignOut} className="hover:bg-accent hover:text-accent-foreground cursor-pointer text-red-600 focus:text-red-600">
<LogOut className="h-4 w-4 mr-2" />
Sign Out
</DropdownMenuItem>
</DropdownMenuContent>
</DropdownMenu>
)}
{user ? (
<div className="flex items-center gap-1 sm:gap-2">
{/* Notification Bell — desktop renders here; mobile renders here too (single instance) */}
<NotificationDropdown profileMode={profileMode ?? undefined} instanceId={isDesktop ? "desktop" : "mobile"} />

{/* User Avatar / Name dropdown */}
<DropdownMenu>
<DropdownMenuTrigger asChild>
<Button
variant="ghost"
size="sm"
className="flex items-center gap-1.5 px-2 sm:px-3 h-9 rounded-full text-white/90 hover:bg-white/10 hover:text-white"
>
<Avatar className="w-7 h-7 shrink-0">
<AvatarImage src={profileAvatarUrl || undefined} alt="Profile" className="object-cover rounded-full" />
<AvatarFallback
className="bg-kanfy-tint-teal text-kanfy-tint-teal-fg text-xs font-bold"
>
{(profileFirstName || user.user_metadata?.first_name || user.email?.split('@')[0] || 'U').charAt(0).toUpperCase()}
</AvatarFallback>
</Avatar>
{profileMode === 'helper' && <OrbitSparkle size={20} />}
<span className="hidden sm:inline text-sm font-medium max-w-[80px] truncate">
{profileFirstName || user.user_metadata?.first_name || user.email?.split('@')[0]}
</span>
<ChevronDown className="h-3 w-3 hidden sm:block shrink-0 text-muted-foreground" />
</Button>
</DropdownMenuTrigger>
<DropdownMenuContent align="end" className="w-56 bg-background border border-border shadow-lg z-50">
<DropdownMenuLabel>
<p className="font-semibold text-sm">{profileFirstName || user.user_metadata?.first_name}</p>
<p className="text-xs text-muted-foreground font-normal truncate">{user.email}</p>
</DropdownMenuLabel>
<DropdownMenuSeparator />

{profileMode === 'helper' ? (
<>
{/* A non-approved helper hits HelperRoute's "Access Restricted" wall at
    /helper-dashboard, so send them to the application status page instead. */}
<DropdownMenuItem onClick={() => { navigate(helperProfileStatus === 'approved' ? '/helper-dashboard' : '/helper-profile'); }}>
<LayoutDashboard className="h-4 w-4 mr-2" />
Helper Dashboard
</DropdownMenuItem>
{/* /my-bookings is the CLIENT's bookings list. In helper mode this sent
    helpers to the wrong side of the marketplace; their jobs live on the
    helper dashboard's bookings tab. */}
<DropdownMenuItem onClick={() => { navigate(helperProfileStatus === 'approved' ? '/helper-dashboard?tab=bookings' : '/helper-profile'); }}>
<Calendar className="h-4 w-4 mr-2" />
My Jobs
</DropdownMenuItem>
<DropdownMenuItem onClick={() => { navigate('/messages'); }}>
<MessageSquare className="h-4 w-4 mr-2" />
Messages
</DropdownMenuItem>
<DropdownMenuSeparator />
<DropdownMenuItem onClick={() => { navigate('/helper-profile'); }}>
<User className="h-4 w-4 mr-2" />
Update Profile
</DropdownMenuItem>
<DropdownMenuItem onClick={() => { navigate('/helper-account'); }}>
<Settings className="h-4 w-4 mr-2" />
Account Settings
</DropdownMenuItem>
</>
) : (
<>
<DropdownMenuItem onClick={() => { navigate('/client-dashboard'); }}>
<LayoutDashboard className="h-4 w-4 mr-2" />
Client Dashboard
</DropdownMenuItem>
<DropdownMenuItem onClick={() => { navigate('/my-bookings'); }}>
<MessageSquare className="h-4 w-4 mr-2" />
My Bookings
</DropdownMenuItem>
<DropdownMenuItem onClick={() => { navigate('/my-helpers'); }}>
<User className="h-4 w-4 mr-2" />
Favourite Helpers
</DropdownMenuItem>
<DropdownMenuItem onClick={() => { navigate('/messages'); }}>
<MessageSquare className="h-4 w-4 mr-2" />
Messages
</DropdownMenuItem>
<DropdownMenuSeparator />
{/* "Edit Profile" -> /client-profile was removed here: that route is the
    first-time onboarding wizard, and using it to edit an existing profile
    meant clicking through all 3 steps and pressing "Complete & Browse
    Services" to save — which re-fired the welcome email and redirected to
    /browse-helpers. Account Settings is now the single client edit
    surface; BottomNav's "Profile" tab already points there, so mobile
    discoverability is unchanged. */}
<DropdownMenuItem onClick={() => { navigate('/client-account'); }}>
<Settings className="h-4 w-4 mr-2" />
Account Settings
</DropdownMenuItem>
</>
)}

<DropdownMenuSeparator />
<DropdownMenuItem onClick={() => navigate('/support')}>
<MessageSquare className="h-4 w-4 mr-2" />
Help Centre
</DropdownMenuItem>
<DropdownMenuSeparator />
<DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive">
<LogOut className="h-4 w-4 mr-2" />
Sign Out
</DropdownMenuItem>
</DropdownMenuContent>
</DropdownMenu>
</div>
) : (
<>
{/* Was `hidden sm:inline-flex` — on a phone this button did not render
    at all, and both real controls (Log In and Get Started) lived only
    inside the hamburger sheet, so a returning visitor had to open a
    menu to find sign-in. Narrower padding below `sm` (matching Get
    Started's existing px-4 sm:px-5) keeps both buttons plus the
    hamburger trigger from crowding a 360px screen now that this is
    always visible. */}
<Button
variant="ghost"
size="sm"
onClick={() => handleOpenAuthModal()}
className="inline-flex rounded-full px-2.5 sm:px-4 text-foreground border-border hover:bg-secondary hover:text-foreground font-medium"
>
Log In
</Button>
<Button
size="sm"
onClick={() => handleOpenAuthModal()}
/* The colour is set inline alongside the background, not left to
   `text-white`: redesign.css paints every font-semibold element in the
   header with --foreground at a specificity a utility class cannot beat,
   which rendered this CTA as ink-on-clay (2.52:1). Inline wins. */
className="rounded-full px-4 sm:px-5 font-semibold text-sm shadow-sm"
style={{ background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }}
>
Get Started
</Button>
</>
)}

{/* ── Mobile / Tablet hamburger sheet ──────────────────── */}
<Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
<SheetTrigger asChild>
<Button variant="ghost" size="sm" className="lg:hidden p-2 text-foreground/70 hover:text-foreground hover:bg-muted" aria-label="Open navigation menu">
<Menu className="h-5 w-5" />
</Button>
</SheetTrigger>
<SheetContent side="right" className="w-[300px] sm:w-[380px] overflow-y-auto">
<SheetHeader className="pb-4 border-b">
<SheetTitle className="text-left flex items-center gap-2">
{/* Real mark, not the retired "TH" tile on a pre-Kanfy navy gradient. */}
<KanfyLogo variant="auto" size="sm" />
Menu
</SheetTitle>
</SheetHeader>

<div className="flex flex-col space-y-1 pt-4">

{/* Mode switch — mobile */}
{user && profileMode && (
<div className="mb-3 px-1">
<Button
variant="outline"
className="w-full rounded-xl border-primary/30 text-primary bg-primary/5 hover:bg-primary/15 font-medium"
onClick={() => { handleModeSwitch(); setIsMobileMenuOpen(false); }}
disabled={isSwitching}
>
{profileMode === 'helper' ? '⇄ Switch to Client Mode' : '⇄ Switch to Helper Mode'}
</Button>
</div>
)}

{/* For Customers */}
<div className="space-y-1">
<h3 className="font-semibold text-xs text-muted-foreground uppercase tracking-wider px-3 py-2">For Customers</h3>
<Button variant="ghost" className="w-full justify-start rounded-xl h-11" onClick={() => { navigate('/booking-flow'); setIsMobileMenuOpen(false); }}>
Find Services
</Button>
<Button variant="ghost" className="w-full justify-start rounded-xl h-11" onClick={() => { navigate('/how-it-works'); setIsMobileMenuOpen(false); }}>
How to Book
</Button>
<Button variant="ghost" className="w-full justify-start rounded-xl h-11" onClick={() => { navigate('/safety'); setIsMobileMenuOpen(false); }}>
Safety &amp; Trust
</Button>
<Button variant="ghost" className="w-full justify-start rounded-xl h-11" onClick={() => { navigate('/faq'); setIsMobileMenuOpen(false); }}>
FAQ
</Button>
</div>

{/* For Helpers */}
<div className="space-y-1 pt-3 border-t">
<h3 className="font-semibold text-xs text-muted-foreground uppercase tracking-wider px-3 py-2">For Helpers</h3>
{!isAdmin && (
<Button variant="ghost" className="w-full justify-start rounded-xl h-11" onClick={() => { navigate('/become-helper'); setIsMobileMenuOpen(false); }}>
Become a Helper
</Button>
)}
<Button variant="ghost" className="w-full justify-start rounded-xl h-11" onClick={() => { navigate('/how-it-works'); setIsMobileMenuOpen(false); }}>
How It Works
</Button>
{/* "Find Jobs" (/jobs board) retired from navigation — see desktop menu note. */}
{profileMode !== 'client' && (
<Button variant="ghost" className="w-full justify-start rounded-xl h-11" onClick={() => { navigate('/helper-resources'); setIsMobileMenuOpen(false); }}>
Helper Resources
</Button>
)}
</div>

{/* Appearance — mobile */}
<div className="space-y-1 pt-3 border-t">
<h3 className="font-semibold text-xs text-muted-foreground uppercase tracking-wider px-3 py-2">Appearance</h3>
<Button
variant="ghost"
className="w-full justify-start rounded-xl h-10"
onClick={() => { setTheme(resolvedTheme === 'dark' ? 'light' : 'dark'); setIsMobileMenuOpen(false); }}
>
{resolvedTheme === 'dark' ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
{resolvedTheme === 'dark' ? 'Light Mode' : 'Dark Mode'}
</Button>
</div>

{/* Help — phones have no floating Help button (owner, 12 Oct), so the
    panel opens from here; the Support page where no widget is mounted. */}
<div className="space-y-1 pt-3 border-t">
<Button
variant="ghost"
className="w-full justify-start rounded-xl h-11"
onClick={() => {
setIsMobileMenuOpen(false);
if (!openHelp()) navigate('/support');
}}
>
<HelpCircle className="h-4 w-4 mr-2" />
Help &amp; support
</Button>
</div>

{/* Language — a single dropdown, not the full list of every language taking
    up the menu. Collapsed it shows the current language; open it lists the
    rest (mirrors the desktop language switcher above). */}
<div className="space-y-1 pt-3 border-t">
<h3 className="font-semibold text-xs text-muted-foreground uppercase tracking-wider px-3 py-2">Language</h3>
{(() => {
const languageOptions = [
{ code: 'en-UG', label: '🇺🇬 English (UG)' },
{ code: 'en-US', label: '🇺🇸 English (US)' },
{ code: 'fr', label: '🇫🇷 French' },
{ code: 'sw', label: '🇰🇪 Swahili' },
{ code: 'ar', label: '🇸🇦 Arabic' },
];
const current = languageOptions.find((o) => o.code === language);
return (
<DropdownMenu>
<DropdownMenuTrigger asChild>
<Button variant="outline" className="w-full justify-between rounded-xl h-11 text-sm font-normal">
<span className="flex items-center gap-2 truncate"><Globe className="h-4 w-4 opacity-70 shrink-0" />{current?.label ?? getLanguageLabel(language)}</span>
<ChevronDown className="h-4 w-4 opacity-60 shrink-0" />
</Button>
</DropdownMenuTrigger>
<DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
{languageOptions.map(({ code, label }) => (
<DropdownMenuItem
key={code}
className="cursor-pointer"
onClick={() => setLanguage(code as Parameters<typeof setLanguage>[0])}
>
<span className={language === code ? 'font-semibold' : ''}>{label}</span>
{language === code && <Check className="h-4 w-4 ml-auto text-primary" />}
</DropdownMenuItem>
))}
</DropdownMenuContent>
</DropdownMenu>
);
})()}
</div>

{/* Account — signed-in only now. Log In / Get Started used to be
    duplicated here (they were the ONLY visible copies on a phone,
    since the top-bar versions were `hidden` below `sm`). Now that the
    top-bar pair is always visible, repeating them here would just show
    the same two buttons twice on one screen. */}
{user && (
<div className="space-y-2 pt-3 border-t pb-6">
<h3 className="font-semibold text-xs text-muted-foreground uppercase tracking-wider px-3 py-2">Account</h3>
<Button
variant="ghost"
className="w-full justify-start rounded-xl h-11"
// Account Settings follows the mode you are CURRENTLY in, not what you
// are capable of. This used to query is_helper and send anyone with the
// flag to /helper-account — so a person who is both a helper and a client
// could never reach their client account page from this menu at all. They
// landed on the helper screen and reported "my client profile shows my
// working areas", which is exactly what it was showing them.
//
// profileMode is already derived from the route above ("Derive mode from
// route — no network call needed"), so this also drops a query.
onClick={() => {
navigate(profileMode === 'helper' ? '/helper-account' : '/client-account');
setIsMobileMenuOpen(false);
}}
>
<Settings className="h-4 w-4 mr-2" />
Account Settings
</Button>
<Button
variant="outline"
className="w-full rounded-xl h-11 text-red-600 border-red-200 hover:bg-red-50"
onClick={() => { handleSignOut(); setIsMobileMenuOpen(false); }}
>
<LogOut className="h-4 w-4 mr-2" />
Sign Out
</Button>
</div>
)}

</div>
</SheetContent>
</Sheet>

</div>
</div>
</div>

{/* Auth Modal */}
<AuthModal
isOpen={isAuthModalOpen}
onClose={() => setIsAuthModalOpen(false)}
/>
</header>
);
  }
